import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import InventoryIcon from "@mui/icons-material/Inventory2";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import NodeSkeleton from "./NodeSkeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { Position, type NodeProps } from "@xyflow/react";
import { useTheme } from "@mui/material/styles";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import { getProductionOrderStatusChipStyle } from "./productionOrderStatusColors";
import NodeListCard from "./NodeListCard";
import NodeToolbar from "./NodeToolbar";

function ProductionOrderListNode({ data }: NodeProps<ExplorerNode>) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const orders = data.productionOrders ?? [];
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            source="d365"
            minWidth={240}
            targetPosition={Position.Top}
            targetHandleId="top"
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
                onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
            />

            {isLoading ? (
                <NodeSkeleton rows={2} variant="list" />
            ) : orders.length === 0 ? (
                <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                        alignItems: "center",
                        mt: 0.5,
                        px: 0.5,
                    }}
                >
                    <InventoryIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Fulfilled from stock &mdash; no production order
                    </Typography>
                </Stack>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {orders.map((order, index) => (
                        <React.Fragment key={order.productionOrderNumber}>
                            {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <ListItem disableGutters sx={{ display: "block", py: 0.5, px: 0.5 }}>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {order.productionOrderNumber}
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {order.productionOrderStatus && (
                                        <Chip
                                            label={order.productionOrderStatus}
                                            size="small"
                                            sx={{
                                                height: 16,
                                                fontSize: "0.65rem",
                                                fontWeight: 600,
                                                ...getProductionOrderStatusChipStyle(order.productionOrderStatus, isDark),
                                            }}
                                        />
                                    )}
                                </Stack>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        display: "block",
                                        opacity: 0.85,
                                    }}
                                >
                                    {order.itemNumber}
                                </Typography>
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            opacity: 0.7,
                                        }}
                                    >
                                        Qty {order.estimatedQuantity}
                                    </Typography>
                                    {order.dataAreaId && (
                                        <Chip
                                            label={order.dataAreaId.toUpperCase()}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 16, fontSize: "0.6rem", fontWeight: 600 }}
                                        />
                                    )}
                                </Stack>
                            </ListItem>
                        </React.Fragment>
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(ProductionOrderListNode);
